// Returns a flat map of field-path -> error string, empty when the form is valid. Same contract
// as the console's isValidCreateTemplateForm (gs-ui-templates/.../validateCreateTemplateForm.ts),
// with two of its bugs fixed rather than copied — see the two comments below.

import {
  BUTTON_TYPE,
  HEADER_TYPE,
  MEDIA_TYPE,
  isAuthentication,
  isPasskey,
  isCarousel,
  isLto,
  isCopyCode,
  isAutofill,
  isZeroTap,
  isMarketing,
  isLinkTracked,
  hasRequestContactInfo,
} from './state';

// Lowercase-only, matching the console's own TEMPLATE_NAME_REGEX (config.ts:278) rather than the
// looser DTO (create-template.dto.ts:17) — deliberate, for UX parity with the console's live error.
// Safe: templateName is locked in edit mode (TemplateName.js), never re-submitted after creation.
const TEMPLATE_NAME_REGEX = /^[a-z0-9_]{3,250}$/;
const HEADER_TEXT_REGEX = /^(?!.*```).*[^\n\r\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F*_~]+$/;
const FOOTER_TEXT_REGEX = /^[^\n]*$/;
// Deliberate divergence from the console's own EMOJI_REGEX (gs-ui-templates config.ts) — that
// pattern is a console BUG, not a rule worth copying: its [U+2000-U+3300] range blocks ordinary
// punctuation that happens to share the same Unicode block as emoji, including the Rupee sign
// (U+20B9), curly quotes, the ellipsis, the trade mark sign, arrows and CJK punctuation. A real
// live footer ("Tap a button below — we're here to help!") gets blocked on the em dash alone.
//
// A real "does this render as emoji" check is a Unicode PROPERTY, not a code-point range: either
//   - the character has Emoji_Presentation (renders as emoji BY DEFAULT), or
//   - the character is Extended_Pictographic AND is immediately followed by U+FE0F, the variation
//     selector that forces emoji style onto an otherwise text-default pictograph.
// Extended_Pictographic ALONE is not enough to decide this: the copyright sign (U+00A9), the
// registered sign (U+00AE) and the trade mark sign (U+2122) all carry Extended_Pictographic
// despite rendering as plain text by default (no trailing U+FE0F) — a bare
// p{Extended_Pictographic} property class alone would still block "Brand(tm)" in a footer, the
// exact class of false positive this rewrite exists to remove. U+FE0F is built via
// String.fromCharCode rather than typed as a literal/escaped character in source — an invisible
// combining character sitting directly in source text is exactly what a copy/paste or formatter
// pass could silently corrupt without ever looking different in a diff.
const VARIATION_SELECTOR_16 = String.fromCharCode(0xfe0f);
// No 'g'/'y' flag — so there's no lastIndex to advance between the header check and the footer
// check sharing this one module-scope instance (both call sites are .test()-only, never
// .replace()/.matchAll()/.exec()-in-a-loop).
const EMOJI_REGEX = new RegExp(
  '\\p{Emoji_Presentation}|\\p{Extended_Pictographic}' + VARIATION_SELECTOR_16,
  'u',
);
const CURLY_BRACES_REGEX = /[{}]/;

// Format-only regexes for the checkers below — verified against the actual console source in a
// prior survey, not re-derived or approximated. No 'g' flag on any of these: same statefulness
// trap as EMOJI_REGEX above, and none of these need it (each is called via a single .test() per
// value, never .replace()/.matchAll()/.exec()-in-a-loop).
const URL_FORMAT_REGEX = /(https?:\/\/)([\w-])+\.([a-zA-Z0-9]{0,63})([\/\w-]+)\/?\??([^#\n\r,]+)?#?([^\n\r,]*)/;
// Accepts an optional leading '+' (international format, e.g. "+911234567890") ahead of digits
// only. Bug fix: the bare digits-only version blocked every real "+"-prefixed number
// (only_numbers_allowed) — gs-intg's own PhoneNumberButtonDto is `@IsString() @MinLength(5)`,
// no format decorator at all, so the '+' restriction was a client-only guess with no backend
// basis. CarouselCardButtonDto's phone_number goes through this same checker (Carousel.js) — see
// validateCarousel's own phone block below for why the min-length floor also needs to change
// alongside this, not just the format.
const PHONE_FORMAT_REGEX = /^\+?\d+$/;
// Enterprise appends `{{1}}` straight onto a DYNAMIC button's base url with no separator
// (GraphAPIHelper.java:220-224 carousel cards, MessageTemplateService.java:2423 top-level CTA). A
// base with no path puts the variable inside the hostname (Meta rejects it as "not a valid URI");
// a base already containing "{{" doubles up. DYNAMIC url buttons only, checked in
// checkDynamicUrlBase below after checkUrlFormat already passed.
const DYNAMIC_URL_PATH_REGEX = /^https?:\/\/[^/?#\s]+[/?#]/i;
// Android deep links use custom app schemes (myapp://product/123), not necessarily http(s). The
// console's own deep-link field reuses its generic URL checker (http/https only), which would
// reject a real deep link — that's a console bug, not a rule to copy forward; this requires a
// scheme (a letter, then letters/digits/+/-/.), a colon, and at least one non-whitespace character
// after it, matching RFC 3986's general URI shape rather than http(s) specifically.
const DEEP_LINK_FORMAT_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:\S+$/;
const COPY_CODE_FORMAT_REGEX = /^[a-zA-Z0-9]+$/;
const VOICE_CALL_DAYS_REGEX = /^([1-9]|[12][0-9]|30)$/;
const PACKAGE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
const SIGNATURE_HASH_REGEX = /^[a-zA-Z0-9/+=]{11}$/;

const BODY_MAX_CHARS = 1024;
const HEADER_FOOTER_MAX_CHARS = 60;
const BUTTON_TEXT_MAX_CHARS = 25;
const TOTAL_BUTTONS_MAX = 10;
// DTO-exact (PhoneNumberButtonDto.phone_number, gs-intg dto/components/phone-number-button.dto.ts:15,
// @MinLength(5)). CarouselCardButtonDto's own phone_number carries no such decorator, but
// validateCarousel applies this same floor to it anyway — see that check's own comment.
const PHONE_NUMBER_MIN_LENGTH = 5;

// Caps the DTOs don't enforce themselves (a 400 would be the only feedback otherwise) — task-
// specified: URL 2, phone 1, copy-code 1, flow 1, voice-call 1.
const CTA_BUTTON_TYPE_MAX = {
  [BUTTON_TYPE.URL]: 2,
  [BUTTON_TYPE.PHONE_NUMBER]: 1,
  [BUTTON_TYPE.COPY_CODE]: 1,
  [BUTTON_TYPE.FLOW]: 1,
  [BUTTON_TYPE.VOICE_CALL]: 1,
};

function isBlank(value) {
  return !value || value.trim() === '';
}

// ---- Format-only checkers ---------------------------------------------------------------------
// Shared by this file's own submit-time aggregate below AND the form's live, per-keystroke
// validation (section components import these directly) — the one place each rule's regex/logic
// lives, so the two call sites can never drift the way the real console's footer and submit
// checks did. Every checker returns '' for a blank value: required-ness is validateForm's concern
// alone, decided separately and only at submit time.

// DTO-exact template-name format check, extracted out of validateForm's required-ness+format
// chain below so the live per-field task can import the identical regex/logic instead of
// re-typing it. See the "Bug fix" comment at validateForm's own call site for why the format
// check must actually run (the console's version silently skips it).
export function checkTemplateNameFormat(value) {
  if (isBlank(value)) return '';
  return TEMPLATE_NAME_REGEX.test(value) ? '' : 'invalid_template_name';
}

// Extracted out of validateForm's HEADER_TYPE.TEXT block below, unchanged logic.
export function checkHeaderTextFormat(value) {
  if (isBlank(value)) return '';
  return (!HEADER_TEXT_REGEX.test(value.trim()) || EMOJI_REGEX.test(value))
    ? 'message_header_cannot_have_new_lines'
    : '';
}

// Extracted out of validateForm's footer block below. Two distinct checks now, matching the
// console's own distinction (Footer/index.tsx:43-58): curly braces (dynamic variables) checked
// FIRST, then newlines/emoji. Note: the console's own SUBMIT-time footer validator borrows the
// *header* error key by mistake (message_header_cannot_have_new_lines), so it shows "header"
// wording for what is actually a footer error — we deliberately use the correct footer wording
// (message_footer_cannot_have_emoji) on both the live and submit tiers instead of copying that bug.
export function checkFooterTextFormat(value) {
  if (isBlank(value)) return '';
  if (CURLY_BRACES_REGEX.test(value)) return 'dynamic_variables_are_not_allowed';
  if (!FOOTER_TEXT_REGEX.test(value) || EMOJI_REGEX.test(value)) return 'message_footer_cannot_have_emoji';
  return '';
}

// No format/clamp check existed anywhere in this file before now — validateAuth only ever checked
// presence. Added new, matching the console's own verified behavior: a non-digit value is a real
// format error, but a value outside the 1-90 range is never rejected — it's silently clamped into
// range instead (see clampExpirationTime below). That's a correction, not an error, which is why
// it isn't folded into this (value) => errorString function.
export function checkExpirationTimeFormat(value) {
  if (isBlank(value)) return '';
  return /^\d+$/.test(value.trim()) ? '' : 'only_numbers_are_allowed_between';
}

// Deliberate non-error UX choice, not a bug — see checkExpirationTimeFormat above. (value) =>
// value, not (value) => errorString: callers apply this on change/blur and write the result back
// into state, they never pass it to setError.
export function clampExpirationTime(value) {
  if (isBlank(value)) return value;
  const numeric = Number(value.trim());
  if (Number.isNaN(numeric)) return value;
  return String(Math.min(90, Math.max(1, Math.trunc(numeric))));
}

export function checkUrlFormat(value) {
  if (isBlank(value)) return '';
  return URL_FORMAT_REGEX.test(value) ? '' : 'invalid_url';
}

// DYNAMIC url buttons only (top-level CTA and carousel card) — never STATIC or
// DYNAMIC_LINK_TRACK. Callers run this after checkUrlFormat has already passed.
export function checkDynamicUrlBase(value) {
  if (isBlank(value)) return '';
  if (!DYNAMIC_URL_PATH_REGEX.test(value)) return 'dynamic_url_missing_path';
  if (CURLY_BRACES_REGEX.test(value)) return 'dynamic_url_contains_braces';
  return '';
}

// Android deep link only (button's own deepLink field) — never the fallback URL, which stays a
// plain checkUrlFormat http(s) check. See DEEP_LINK_FORMAT_REGEX's own comment for why this
// diverges from checkUrlFormat instead of reusing it.
export function checkDeepLinkFormat(value) {
  if (isBlank(value)) return '';
  return DEEP_LINK_FORMAT_REGEX.test(value) ? '' : 'invalid_deep_link';
}

export function checkPhoneFormat(value) {
  if (isBlank(value)) return '';
  return PHONE_FORMAT_REGEX.test(value) ? '' : 'only_numbers_allowed';
}

// Significant-digit count for the PHONE_NUMBER_MIN_LENGTH floor below — excludes a leading '+' so
// a real international number ("+911234567890", 12 digits) isn't penalised for carrying one.
// Only ever called after checkPhoneFormat has already passed, so `value` is known to already
// match PHONE_FORMAT_REGEX (an optional single leading '+' then digits) — no re-validation here.
function phoneDigitCount(value) {
  return value.startsWith('+') ? value.length - 1 : value.length;
}

export function checkCopyCodeFormat(value) {
  if (isBlank(value)) return '';
  return COPY_CODE_FORMAT_REGEX.test(value)
    ? ''
    : 'no_space_or_special_characters';
}

export function checkVoiceCallDaysFormat(value) {
  if (isBlank(value)) return '';
  return VOICE_CALL_DAYS_REGEX.test(value) ? '' : 'only_numbers_with_months_days_allowed';
}

export function checkPackageNameFormat(value) {
  if (isBlank(value)) return '';
  return PACKAGE_NAME_REGEX.test(value) ? '' : 'invalid_package_name_format';
}

export function checkSignatureHashFormat(value) {
  if (isBlank(value)) return '';
  return SIGNATURE_HASH_REGEX.test(value) ? '' : 'invalid_signature_hash_format';
}

function validateButtons(state, setError) {
  const { buttons } = state;
  if (buttons.length > TOTAL_BUTTONS_MAX) {
    setError('buttons', 'too_many_buttons');
  }

  // Opt-out's quick reply is appended at payload-build time (payload.js's bucketButtons), never
  // stored as a row in state.buttons (state.js's own optOutButton comment) — so state.buttons.length
  // alone can sit right at TOTAL_BUTTONS_MAX while the real wire payload (buttons.length + 1) still
  // overflows it. Checked independently of the plain overflow above so it still fires even when
  // buttons.length itself is within range. isMarketing() gates it the same way payload.js gates the
  // append, so a stray optOutButton:true left over from switching category away from Marketing can
  // never block submission here for a button that will never actually be sent. There is no
  // separately-enforced "quick reply" cap distinct from this total to also check — ce-api defines
  // getNoOfQuickReplyButtonsToBeAllowed()/getNoOfQRButtonAllowed() but neither has a single call
  // site outside their own definitions (verified against ReloadableProperties.java and
  // MessageTemplate.java); the only limit actually enforced server-side is the combined
  // qrTotal+ctaTotal check against getNoOfButtonAllowed(), i.e. this same TOTAL_BUTTONS_MAX.
  if (isMarketing(state) && state.optOutButton && (buttons.length + 1) > TOTAL_BUTTONS_MAX) {
    setError('buttons.optOutButton', `opt_out_button_exceeds_button_limit:${TOTAL_BUTTONS_MAX}`);
  }

  const countByType = {};
  buttons.forEach((btn, index) => {
    countByType[btn.type] = (countByType[btn.type] || 0) + 1;
    const path = `buttons.${index}`;

    // Every button needs text except PASSKEY (fixed server-side to "Verify") and COPY_CODE (console
    // locks the displayed label to COPY_CODE_BUTTON_LABEL; payload.js never sends `text` for it —
    // ce-api's CopyOfferCodeButton.validate() only ever checks `example`, never text).
    // Bug fix: an OTP button's `text` is the copy-code/backup-code button's own title (AuthButtonsText.js
    // labels the field "Copy code button text" for every OTP-shaped subcategory) — the console's
    // own isValidButtonText gives it the dedicated copy_code_text_is_required key
    // (validateCreateTemplateForm.ts:393-401), not the generic button_title_is_required this used
    // to emit for every type alike.
    if (btn.type !== BUTTON_TYPE.PASSKEY && btn.type !== BUTTON_TYPE.COPY_CODE && isBlank(btn.text.value)) {
      setError(`${path}.text.value`, btn.type === BUTTON_TYPE.OTP ? 'copy_code_text_is_required' : 'button_title_is_required');
    }
    // Same exemption for COPY_CODE here: its text is never sent on the wire (see payload.js), so a
    // length error on a field that can't reach the server either way is pure friction. (An empty
    // value could never have hit this branch anyway — the truthy guard below already skips it.)
    if (btn.type !== BUTTON_TYPE.COPY_CODE && btn.text.value && btn.text.value.length > BUTTON_TEXT_MAX_CHARS) {
      setError(`${path}.text.value`, `max_character_allowed:${BUTTON_TEXT_MAX_CHARS}`);
    }

    if (btn.type === BUTTON_TYPE.URL) {
      if (isBlank(btn.urlType.value)) {
        setError(`${path}.urlType.value`, 'url_type_is_required');
      }
      // DYNAMIC_LINK_TRACK: Enterprise fills url/example itself and ignores ours
      // (URLButton.java:166-199); sections/Buttons.js doesn't even render buttonValue/suffix for
      // this type, so there's nothing to validate.
      if (!isLinkTracked(btn)) {
        if (isBlank(btn.buttonValue.value)) {
          setError(`${path}.buttonValue.value`, 'url_is_required');
        } else {
          const urlError = checkUrlFormat(btn.buttonValue.value);
          if (urlError) {
            setError(`${path}.buttonValue.value`, urlError);
          } else if (btn.urlType.value === 'DYNAMIC') {
            const dynamicUrlError = checkDynamicUrlBase(btn.buttonValue.value);
            if (dynamicUrlError) setError(`${path}.buttonValue.value`, dynamicUrlError);
          }
        }
        if (btn.urlType.value === 'DYNAMIC' && isBlank(btn.suffix.value)) {
          setError(`${path}.suffix.value`, 'suffix_is_required');
        }
      }
      // Deep link is independent of urlType/isLinkTracked — Enterprise keeps app_deep_link on a
      // URL button regardless of which urlType that same button otherwise uses
      // (MessageTemplateService.java:2429-2438), so this checks purely off hasDeepLink, never
      // nested inside the isLinkTracked block above.
      if (btn.hasDeepLink) {
        if (isBlank(btn.deepLink.value)) {
          setError(`${path}.deepLink.value`, 'url_is_required');
        } else {
          const deepLinkError = checkDeepLinkFormat(btn.deepLink.value);
          if (deepLinkError) setError(`${path}.deepLink.value`, deepLinkError);
        }
        // Fallback URL is optional — only format-checked when the user actually typed one.
        if (!isBlank(btn.fallbackLink.value)) {
          const fallbackLinkError = checkUrlFormat(btn.fallbackLink.value);
          if (fallbackLinkError) setError(`${path}.fallbackLink.value`, fallbackLinkError);
        }
      }
    }
    if (btn.type === BUTTON_TYPE.PHONE_NUMBER) {
      if (isBlank(btn.buttonValue.value)) {
        setError(`${path}.buttonValue.value`, 'phone_is_required');
      } else {
        const phoneError = checkPhoneFormat(btn.buttonValue.value);
        if (phoneError) {
          setError(`${path}.buttonValue.value`, phoneError);
        } else if (phoneDigitCount(btn.buttonValue.value) < PHONE_NUMBER_MIN_LENGTH) {
          // Bug fix: no length floor was checked here before — Buttons.js's own `minLength: 5`
          // is an HTML attribute that never fires (submit bypasses native validation), so a
          // 1-4 digit value sailed through client-side and only 400'd server-side. Not console
          // copy: the console's phone field includes the country code, so it only ever needs
          // presence (phone_is_required); our DTO enforces this 5-digit floor regardless.
          // Counted via phoneDigitCount, not .length, so a real "+"-prefixed number isn't
          // penalised one digit for carrying the country-code marker.
          setError(`${path}.buttonValue.value`, `phone_number_min_length:${PHONE_NUMBER_MIN_LENGTH}`);
        }
      }
    }
    if (btn.type === BUTTON_TYPE.COPY_CODE) {
      if (isBlank(btn.buttonValue.value)) {
        setError(`${path}.buttonValue.value`, 'copy_code_is_required');
      } else {
        const copyCodeError = checkCopyCodeFormat(btn.buttonValue.value);
        if (copyCodeError) setError(`${path}.buttonValue.value`, copyCodeError);
      }
    }
    if (btn.type === BUTTON_TYPE.FLOW) {
      if (isBlank(btn.buttonValue.value)) {
        setError(`${path}.buttonValue.value`, 'screen_name_is_required');
      }
      if (isBlank(btn.flowId.value)) {
        setError(`${path}.flowId.value`, 'flowid_is_required');
      }
    }
    if (btn.type === BUTTON_TYPE.VOICE_CALL && !isBlank(btn.activeFor.value)) {
      // No presence check exists for activeFor (never did, still doesn't) — format-only.
      const voiceCallError = checkVoiceCallDaysFormat(btn.activeFor.value);
      if (voiceCallError) setError(`${path}.activeFor.value`, voiceCallError);
    }
  });

  Object.keys(CTA_BUTTON_TYPE_MAX).forEach((type) => {
    if ((countByType[type] || 0) > CTA_BUTTON_TYPE_MAX[type]) {
      setError(`buttons.${type}`, 'max_buttons_of_type_exceeded');
    }
  });
}

// Bug fix: presence was never checked here before (format-only, same as VOICE_CALL's activeFor
// above) — an empty package name/signature hash silently passed client-side and only 400'd
// server-side ("otpButtons has an OTP button missing autofill_text, app credentials, or zero-tap
// terms acceptance"). Gated on isAutofill/isZeroTap the same way the console's own
// isValidAppsSetup is (validateCreateTemplateForm.ts:414-448) — appsSetup can hold rows left over
// from switching subcategory away from Autofill/Zero-Tap (Category.js never resets it), and the
// App Setup card itself only renders for those two subcategories (AuthButtonsText.js's
// needsAutofillText), so validating a row the user can no longer see would block submission with
// no way to fix it.
function validateAppsSetup(state, setError) {
  if (!(isAutofill(state) || isZeroTap(state))) return;

  // Defensive backstop: AuthAppSetup.js's own mount effect seeds exactly one row the moment
  // Autofill/Zero-Tap is selected (mirroring the console's AppSetup.tsx:39-43), so this should be
  // unreachable in practice — kept in case that effect's own guard is ever weakened.
  if (state.appsSetup.length === 0) {
    setError('appsSetup', 'package_name_is_required');
    return;
  }

  let hasRowError = false;
  state.appsSetup.forEach((app, index) => {
    const path = `appsSetup.${index}`;
    if (isBlank(app.packageName.value)) {
      hasRowError = true;
      setError(`${path}.packageName.value`, 'package_name_is_required');
    } else {
      const packageNameError = checkPackageNameFormat(app.packageName.value);
      if (packageNameError) {
        hasRowError = true;
        setError(`${path}.packageName.value`, packageNameError);
      }
    }
    if (isBlank(app.signatureHash.value)) {
      hasRowError = true;
      setError(`${path}.signatureHash.value`, 'signature_hash_is_required');
    } else {
      const signatureHashError = checkSignatureHashFormat(app.signatureHash.value);
      if (signatureHashError) {
        hasRowError = true;
        setError(`${path}.signatureHash.value`, signatureHashError);
      }
    }
  });

  // Mirrors the console's own gate (validateCreateTemplateForm.ts:438: `!_error &&
  // appsSetup.length > 1 && !isUniquePackageNameAndHash(...)`) — duplicate-pair checking only
  // runs once every row is already individually valid, same as the console skipping it entirely
  // while any row still has a required/format error of its own.
  if (!hasRowError && state.appsSetup.length > 1) {
    const seen = new Set();
    let hasDuplicate = false;
    state.appsSetup.forEach((app) => {
      const key = `${app.packageName.value}\u0000${app.signatureHash.value}`;
      if (seen.has(key)) hasDuplicate = true;
      seen.add(key);
    });
    if (hasDuplicate) {
      state.appsSetup.forEach((app, index) => {
        setError(`appsSetup.${index}.packageName.value`, 'duplicate_package_signature_pair');
        setError(`appsSetup.${index}.signatureHash.value`, 'duplicate_package_signature_pair');
      });
    }
  }
}

function validateCarousel(state, setError, isEdit) {
  // Defensive backstop, same reasoning as validateAppsSetup's own top-of-function guard just
  // above: Carousel.js's own mount effect (`if (carousel && cards.length === 0) { ...seed one
  // blank card... }`) is what normally keeps this unreachable in the browser, but that's a React
  // effect — it never runs against a prefilled state before the form's first paint, and this
  // validator has no way to depend on it having run. There is no server-side backstop either:
  // CarouselCardDto's own @ArrayMinSize(TEMPLATE_LIMITS.carouselCardsMin) (gs-intg dto/
  // template-content.dto.ts:120-128) sits behind that same field's @IsOptional(), so it never
  // runs at all once payload.js omits carouselCards outright for zero cards. Without this check, a
  // carousel-shaped prefill whose source carried no carouselCards (see prefill.js's own
  // button_type==='CAROUSEL' comment) would submit as a template with no carousel content and no
  // error anywhere.
  if (state.carousels.cards.length === 0) {
    setError('carousels.cards', 'carousel_requires_at_least_one_card');
    return;
  }

  state.carousels.cards.forEach((card, cardIndex) => {
    const path = `carousels.cards.${cardIndex}`;
    if (isBlank(card.description.value)) {
      setError(`${path}.description.value`, 'description_is_required');
    }
    if (!card.cardMedia.mediaId) {
      // Bug fix: payload.js:190 sends card media as `header: { id: card.cardMedia.mediaId }` —
      // mediaId alone, never mediaSrc. The old `!mediaSrc && !mediaId` check let a card with only
      // a preview mediaSrc (prefill.js now populates it for display — see buildCarouselCardFromRaw)
      // pass validation and then silently send `id: undefined`. mediaId is the one field that
      // proves a real (re-)upload happened: Carousel.js's uploadCardMedia sets it immediately on a
      // successful upload, before the user can even reach submit, so requiring it here doesn't
      // block a fresh create — only an edit whose media was never re-attached, which is genuinely
      // required (same verified backend contract as the header-media check in validateForm below).
      setError(`${path}.cardMedia`, isEdit
        ? 'existing_media_must_be_reuploaded_to_save_changes'
        : 'card_media_is_required');
    }
    card.variable.forEach((item, varIndex) => {
      if (item.name && isBlank(item.value)) {
        setError(`${path}.variable.${varIndex}.value`, 'sample_variable_is_required');
      }
    });
    // Bug fix: CarouselCardDto.buttons carries @ArrayMinSize(1) (gs-intg dto/components/
    // carousel-card.dto.ts:73) — the console makes zero buttons structurally impossible (its
    // global "No of buttons" select only offers 1-2), but this UI adds/removes buttons per card
    // individually, so nothing stopped a card reaching zero. Keyed like validateButtons' own
    // array-level `buttons`/`buttons.${type}` errors above — no index into card.buttons, since
    // there's no row to attach it to when the array itself is empty.
    if (card.buttons.length === 0) {
      setError(`${path}.buttons`, 'card_button_required');
    }
    card.buttons.forEach((btn, btnIndex) => {
      const btnPath = `${path}.buttons.${btnIndex}`;
      if (isBlank(btn.text.value)) {
        setError(`${btnPath}.text.value`, 'button_title_is_required');
      }
      // DYNAMIC_LINK_TRACK: same exemption as the top-level CTA check above — Enterprise fills
      // url/example itself (URLButton.java:166-199) and Carousel.js doesn't render buttonValue
      // for this type.
      if (btn.type === BUTTON_TYPE.URL && !isLinkTracked(btn)) {
        if (isBlank(btn.buttonValue.value)) {
          setError(`${btnPath}.buttonValue.value`, 'url_is_required');
        } else {
          const urlError = checkUrlFormat(btn.buttonValue.value);
          if (urlError) {
            setError(`${btnPath}.buttonValue.value`, urlError);
          } else if (btn.urlType.value === 'DYNAMIC') {
            const dynamicUrlError = checkDynamicUrlBase(btn.buttonValue.value);
            if (dynamicUrlError) setError(`${btnPath}.buttonValue.value`, dynamicUrlError);
          }
        }
      }
      if (btn.type === BUTTON_TYPE.PHONE_NUMBER) {
        if (isBlank(btn.buttonValue.value)) {
          setError(`${btnPath}.buttonValue.value`, 'phone_is_required');
        } else {
          const phoneError = checkPhoneFormat(btn.buttonValue.value);
          if (phoneError) {
            setError(`${btnPath}.buttonValue.value`, phoneError);
          } else if (phoneDigitCount(btn.buttonValue.value) < PHONE_NUMBER_MIN_LENGTH) {
            // CarouselCardButtonDto's phone_number carries no @MinLength itself, unlike the
            // top-level CTA's PhoneNumberButtonDto — applied anyway for consistency: a number
            // under 5 digits is never a valid phone number regardless of which DTO receives it.
            // Same phoneDigitCount floor as the top-level CTA check above, for the same reason —
            // a "+"-prefixed card phone button must not be penalised one digit for the '+'.
            setError(`${btnPath}.buttonValue.value`, `phone_number_min_length:${PHONE_NUMBER_MIN_LENGTH}`);
          }
        }
      }
    });
  });
}

function validateAuth(state, setError) {
  const otpButtons = state.buttons.filter((btn) => btn.type === BUTTON_TYPE.OTP);

  // "exactly one OTP button" only applies when the chosen subcategory is one of the OTP-shaped
  // ones; isPasskey()/plain quick-reply auth templates carry no OTP button at all.
  if ((isCopyCode(state) || isAutofill(state) || isZeroTap(state)) && otpButtons.length !== 1) {
    setError('buttons.OTP', 'exactly_one_otp_button_required');
  }

  // Passkey never shows the expiration checkbox (AuthMessageContent.js hides the whole Message Content
  // card for isPasskey — see its own comment), but isExpirationTimeChecked isn't reset on a
  // subcategory change, so a flag left over from switching subcategory TO Passkey must not block
  // submission over a field the user can no longer see or fix.
  if (!isPasskey(state) && state.messageContent.isExpirationTimeChecked) {
    if (isBlank(state.messageContent.expirationTime.value)) {
      setError('messageContent.expirationTime.value', 'expiration_time_is_required');
    } else {
      const expirationTimeError = checkExpirationTimeFormat(state.messageContent.expirationTime.value);
      if (expirationTimeError) setError('messageContent.expirationTime.value', expirationTimeError);
    }
  }

  // Bug fix: the console (validateCreateTemplateForm.ts:366-383) nests the zero-tap terms check
  // inside `if (messageContent.isExpirationTimeChecked)`, so unchecking expiration silently skips
  // validating the terms checkbox even though it's still required. Check it independently.
  if (isZeroTap(state)) {
    const otpButton = otpButtons[0];
    if (!otpButton || !otpButton.zeroTapTermsAccepted) {
      setError('buttons.zeroTapTermsAccepted', 'this_box_must_be_checked');
    }
  }

  if (isAutofill(state) || isZeroTap(state)) {
    otpButtons.forEach((btn, index) => {
      if (isBlank(btn.autofillText.value)) {
        setError(`buttons.${index}.autofillText.value`, 'autofill_text_is_required');
      }
    });
  }
}

export function validateForm(state, { isEdit = false } = {}) {
  const errors = {};
  const setError = (path, message) => {
    if (!errors[path]) errors[path] = message;
  };

  if (!isEdit) {
    // Bug fix: the console imports TEMPLATE_NAME_REGEX but its isValidTemplateName
    // (validateCreateTemplateForm.ts:31-47) never calls it — `if (value) { _error = templateName.error; }`
    // overwrites the required-check result with whatever stale error was already on the field,
    // so a name with spaces or punctuation sails through client-side and only 400s at the server.
    if (isBlank(state.templateName.value)) {
      setError('templateName.value', 'template_name_required');
    } else {
      const templateNameError = checkTemplateNameFormat(state.templateName.value);
      if (templateNameError) setError('templateName.value', templateNameError);
    }
    if (isBlank(state.language.value)) {
      setError('language.value', 'language_is_required');
    }
  }

  if (isBlank(state.category.value)) {
    setError('category.value', 'category_is_required');
  }
  if (isBlank(state.category.selectedSubcategory.value)) {
    setError('category.selectedSubcategory.value', 'subcategory_is_required');
  }

  // Bug fix: Authentication templates other than Passkey never render the body field at all
  // (Body.js returns null for isAuthentication(state) && !isPasskey(state) — the auth-extras task
  // owns a fixed, server-supplied body for those). state.body.value stays '' forever for that
  // case, so the required check must not fire for a field the user can never see or fix.
  if (!(isAuthentication(state) && !isPasskey(state))) {
    if (isBlank(state.body.value)) {
      setError('body.value', 'template_body_is_required');
    } else if (state.body.value.length > BODY_MAX_CHARS) {
      setError('body.value', `max_character_allowed:${BODY_MAX_CHARS}`);
    }
  }
  state.body.variable.forEach((item, index) => {
    if (item.name && isBlank(item.value)) {
      setError(`body.variable.${index}.value`, 'sample_variable_is_required');
    }
  });

  if (state.header.value === HEADER_TYPE.TEXT) {
    const text = state.header.headerText.value;
    if (isBlank(text)) {
      setError('header.headerText.value', 'template_header_is_required');
    } else if (text.length > HEADER_FOOTER_MAX_CHARS) {
      setError('header.headerText.value', `max_character_allowed:${HEADER_FOOTER_MAX_CHARS}`);
    } else {
      const headerTextError = checkHeaderTextFormat(text);
      if (headerTextError) setError('header.headerText.value', headerTextError);
    }
    if (state.header.headerText.variable.length > 1) {
      setError('header.headerText.variable', 'invalid_format_only_one_variable_allowed');
    }
    state.header.headerText.variable.forEach((item, index) => {
      if (item.name && isBlank(item.value)) {
        setError(`header.headerText.variable.${index}.value`, 'sample_variable_is_required');
      }
    });
  }
  if (state.header.value === HEADER_TYPE.MEDIA) {
    if (isBlank(state.header.headerMedia.value)) {
      setError('header.headerMedia.value', 'header_media_type_is_required');
    } else if (state.header.headerMedia.value !== MEDIA_TYPE.LOCATION && !state.header.headerMedia.mediaId) {
      // Bug fix: verified against gs-intg-services + ce-api/whatsappmediaapi (the real Enterprise
      // update path, MessageTemplate.java) that omitting media on an update does NOT preserve the
      // template's existing media. validateMediaSample() runs unconditionally for every
      // media-type update and throws a ValidationException (MediaFilePartMissingException) when
      // no multipart header_examples file part is present; the one piece of code that would carry
      // the existing header_examples forward on a blank request is commented out (dead) at
      // MessageTemplate.java:1176-1179. So this check matches a real backend requirement —
      // prefill.js just can never satisfy it (a list row carries no re-fetchable mediaId), which
      // made every edit of a media-header template look broken. Keep the check; on edit, explain
      // why instead of repeating the create-time copy.
      //
      // LOCATION is excluded outright, not just softened: validateMediaSample's own type switch
      // (MessageTemplate.java:2287) only requires a file for IMAGE/VIDEO/DOCUMENT/GIF — LOCATION
      // is absent from that list, so there is no sample media to upload, ever, on create OR edit.
      // mediaId legitimately stays blank forever for this type.
      setError('header.headerMedia.mediaId', isEdit
        ? 'existing_media_must_be_reuploaded_to_save_changes'
        : 'template_sample_media_is_required');
    }
  }

  // Opt-out overwrites the footer unconditionally server-side (MessageTemplate.java:1575-1577 —
  // see state.js's MARKETING_OPT_OUT_FOOTER comment) and payload.js sends that fixed string in
  // place of whatever is in state.footer.footerText.value while it's active, so validating the
  // user's own footer text here would block submission over a value that's never actually sent.
  if (!(isMarketing(state) && state.optOutButton)) {
    const footerText = state.footer.footerText.value;
    if (footerText) {
      if (footerText.length > HEADER_FOOTER_MAX_CHARS) {
        setError('footer.footerText.value', `max_character_allowed:${HEADER_FOOTER_MAX_CHARS}`);
      } else {
        const footerTextError = checkFooterTextFormat(footerText);
        if (footerTextError) setError('footer.footerText.value', footerTextError);
      }
    }
  }

  // Bug fix: payload.js sends messageSendTtlSeconds: Number(validityPeriod.value) * 60 whenever
  // isValidityChecked is true, with no fallback. An unset Select leaves validityPeriod.value ''
  // and Number('') * 60 === 0 — a near-instant expiry that ships silently, since 0 survives
  // payload.js's own omitEmpty (it only strips null/undefined). sections/MessageValidity.js
  // already reads and renders errors['messageValidity.validityPeriod.value'], so this is wired to
  // the UI, not dead.
  if (state.messageValidity.isValidityChecked && isBlank(state.messageValidity.validityPeriod.value)) {
    setError('messageValidity.validityPeriod.value', 'validity_period_is_required');
  }

  if (isLto(state) && isBlank(state.lto.offerTitle.value)) {
    setError('lto.offerTitle.value', 'offer_title_is_required');
  }

  validateButtons(state, setError);

  // Backstop for ValidationUtils.validateRequestContactInfoTemplate: RCI must be the template's
  // ONLY button, with no header and no footer. Buttons.js/Header.js/Footer.js already enforce this
  // live (adding RCI replaces the whole list and hides both sections), so this should be
  // unreachable in the browser — kept in case a prefilled/stale state slips past that UI. Keyed
  // like validateButtons' own per-type `buttons.{TYPE}` cap errors so Buttons.js's existing
  // capErrorKeys rendering picks it up with no extra wiring.
  if (hasRequestContactInfo(state)) {
    const isRciAlone = state.buttons.length === 1 && state.buttons[0].type === BUTTON_TYPE.REQUEST_CONTACT_INFO;
    const hasHeader = state.header.value !== HEADER_TYPE.NONE;
    const hasFooter = Boolean(state.footer.footerText.value);
    if (!isRciAlone || hasHeader || hasFooter) {
      setError(`buttons.${BUTTON_TYPE.REQUEST_CONTACT_INFO}`, 'request_contact_info_must_be_alone');
    }
  }

  // Backstops for MessageTemplate.java's LTO button-sequence checks (config.tpl / :1781-1850):
  // at least one URL CTA, and copy-code first when expiry is on. Category.js/Lto.js already
  // guarantee this shape live (LTO seeds a locked URL button; the expiry checkbox inserts/removes
  // a locked copy-code button at index 0), so this too should be unreachable in the browser.
  if (isLto(state)) {
    if (!state.buttons.some((btn) => btn.type === BUTTON_TYPE.URL)) {
      setError('lto.buttons', 'lto_requires_url_button');
    } else if (state.lto.isExpiryChecked && state.buttons[0]?.type !== BUTTON_TYPE.COPY_CODE) {
      setError('lto.buttons', 'lto_requires_copy_code_first');
    }
  }

  if (isCarousel(state)) {
    validateCarousel(state, setError, isEdit);
  }

  if (isAuthentication(state)) {
    validateAuth(state, setError);
    validateAppsSetup(state, setError);
  }

  if (isEdit && isBlank(state.status.value)) {
    setError('status.value', 'status_is_required');
  }

  return errors;
}
